import React, { useEffect, useState } from "react";
import { Edit, Trash2, Eye, Download, FileText, RotateCcw } from "lucide-react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import "./Carmonitors.css";

const url = process.env.REACT_APP_BACKEND_URL;

const CarMonitors = () => {
  const [monitors, setMonitors] = useState([]);
  const [collectors, setCollectors] = useState([]);
  const [zones, setZones] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [filterCollector, setFilterCollector] = useState("");
  const [filterZone, setFilterZone] = useState("");
  const [filterDistrict, setFilterDistrict] = useState("");
  const [filterStatus, setFilterStatus] = useState(""); // yes/no/pending filter
  const navigate = useNavigate();

  // Fetch monitors
  const fetchMonitors = async () => {
    try {
      const endpoint = showInactive ? `${url}/api/carmonitor/all` : `${url}/api/carmonitor`;
      const res = await axios.get(endpoint);
      setMonitors(res.data.data || []);
    } catch (err) {
      alert(err.response?.data?.message || "Failed to fetch car monitors");
    } finally {
      setLoading(false);
    }
  };

  // Fetch collectors
  const fetchCollectors = async () => {
    try {
      const res = await axios.get(`${url}/api/collectors`);
      setCollectors(res.data.data || []);
    } catch (err) {
      console.error("Error fetching collectors:", err);
    }
  };

  // Fetch zones
  const fetchZones = async () => {
    try {
      const res = await axios.get(`${url}/api/zone`);
      setZones(res.data.data || []);
    } catch (err) {
      console.error("Error fetching zones:", err);
    }
  };

  // Fetch districts
  const fetchDistricts = async () => {
    try {
      const res = await axios.get(`${url}/api/district`);
      setDistricts(res.data.data || []);
    } catch (err) {
      console.error("Error fetching districts:", err);
    }
  };

  useEffect(() => {
    Promise.all([
      fetchMonitors(),
      fetchCollectors(),
      fetchZones(),
      fetchDistricts()
    ]);
  }, [showInactive]);

  // Update fullCover
  const handleFullCoverToggle = async (monitor) => {
    try {
      const res = await axios.put(`${url}/api/carmonitor/${monitor._id}/fullcover`, {
        fullCover: !monitor.fullCover,
      });
      if (res.data.success) {
        fetchMonitors(); // refresh
        alert("Full cover updated successfully");
      }
    } catch (err) {
      alert(err.response?.data?.message || "Failed to update fullCover");
    }
  };

  // Soft delete
  const handleSoftDelete = async (id) => {
    if (window.confirm("Are you sure you want to deactivate this car monitor?")) {
      try {
        const res = await axios.delete(`${url}/api/carmonitor/${id}`);
        alert(res.data.message || "Car monitor deactivated successfully");
        fetchMonitors();
      } catch (err) {
        alert(err.response?.data?.message || "Failed to deactivate car monitor");
      }
    }
  };

  // Restore
  const handleRestore = async (id) => {
    if (window.confirm("Are you sure you want to activate this car monitor?")) {
      try {
        const res = await axios.put(`${url}/api/carmonitor/${id}/restore`);
        alert(res.data.message || "Car monitor activated successfully");
        fetchMonitors();
      } catch (err) {
        alert(err.response?.data?.message || "Failed to activate car monitor");
      }
    }
  };

  // Export to Excel
  const exportToExcel = () => {
    const excelData = filteredMonitors.map((m, index) => ({
      "S.No": index + 1,
      "Collector": m.collector ? `${m.collector.fname || ""} ${m.collector.lname || ""}`.trim() : "N/A",
      "District": m.districtName,
      "Zone": m.zone?.name || "N/A",
      "Tarkono": m.tarkono || "N/A",
      "Full Cover": m.fullCover === true ? "Yes" : m.fullCover === false ? "No" : "Pending",
      "Created At": new Date(m.createdAt).toLocaleString(),
      "Status": m.systemStatus || "active",
    }));

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "CarMonitors");
    
    const date = new Date();
    const fileName = `carmonitors_${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // Apply filters
  const filteredMonitors = monitors.filter((m) => {
    const matchCollector = filterCollector
      ? m.collector?._id === filterCollector
      : true;
    const matchZone = filterZone
      ? m.zone?.name?.toLowerCase().includes(filterZone.toLowerCase())
      : true;
    const matchDistrict = filterDistrict
      ? m.districtName?.toLowerCase().includes(filterDistrict.toLowerCase())
      : true;
    const matchStatus = filterStatus
      ? (filterStatus === "yes" ? m.fullCover === true :
         filterStatus === "no" ? m.fullCover === false :
         m.fullCover === null)
      : true;
    return matchCollector && matchZone && matchDistrict && matchStatus;
  });

  const yesCount = filteredMonitors.filter(m => m.fullCover === true).length;
  const noCount = filteredMonitors.filter(m => m.fullCover === false).length;
  const pendingCount = filteredMonitors.filter(m => m.fullCover === null).length;

  if (loading) return <div className="loading">Loading car monitors...</div>;

  return (
    <div className="car-container">
      <div className="header">
        <h2>Car Monitors</h2>
        <div className="header-actions">
          <button
            onClick={() => setShowInactive(!showInactive)}
            className={`toggle-button ${showInactive ? 'active' : ''}`}
          >
            {showInactive ? 'Showing All' : 'Show Active Only'}
          </button>
          <button className="export-btn" onClick={exportToExcel}>
            <Download size={16} /> Export to Excel
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <select
          className="filter-select"
          value={filterCollector}
          onChange={(e) => setFilterCollector(e.target.value)}
        >
          <option value="">All Collectors</option>
          {collectors.map((c) => (
            <option key={c._id} value={c._id}>
              {c.fname} {c.lname}
            </option>
          ))}
        </select>

        <select
          className="filter-select"
          value={filterZone}
          onChange={(e) => setFilterZone(e.target.value)}
        >
          <option value="">All Zones</option>
          {zones.map((z) => (
            <option key={z._id} value={z.name}>
              {z.name}
            </option>
          ))}
        </select>

        <select
          className="filter-select"
          value={filterDistrict}
          onChange={(e) => setFilterDistrict(e.target.value)}
        >
          <option value="">All Districts</option>
          {districts.map((d) => (
            <option key={d._id} value={d.name}>
              {d.name}
            </option>
          ))}
        </select>

        <select
          className="filter-select"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="">All Status</option>
          <option value="yes">Yes (Full Cover)</option>
          <option value="no">No (No Cover)</option>
          <option value="pending">Pending</option>
        </select>
      </div>

      {/* Stats Summary */}
      <div className="stats-grid">
        <div className="stat-card total">
          <div className="stat-value">{filteredMonitors.length}</div>
          <div className="stat-label">Total</div>
        </div>
        <div className="stat-card yes">
          <div className="stat-value">{yesCount}</div>
          <div className="stat-label">Yes</div>
        </div>
        <div className="stat-card no">
          <div className="stat-value">{noCount}</div>
          <div className="stat-label">No</div>
        </div>
        <div className="stat-card pending">
          <div className="stat-value">{pendingCount}</div>
          <div className="stat-label">Pending</div>
        </div>
      </div>

      <div className="table-container">
        <table className="car-table">
          <thead>
            <tr>
              <th>Collector</th>
              <th>District</th>
              <th>Zone</th>
              <th>Tarkono</th>
              <th>Full Cover</th>
              <th>Created At</th>
              <th>System Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredMonitors.length === 0 ? (
              <tr>
                <td colSpan="8" className="no-data">
                  No car monitors found
                </td>
              </tr>
            ) : (
              filteredMonitors.map((m) => (
                <tr key={m._id} className={m.systemStatus === 'inactive' ? 'inactive-row' : ''}>
                  <td>{m.collector?.fname} {m.collector?.lname}</td>
                  <td>{m.districtName}</td>
                  <td>{m.zone?.name || "N/A"}</td>
                  <td>{m.tarkono || "N/A"}</td>
                  <td>
                    <span className={`status-badge ${m.fullCover === true ? 'yes' : m.fullCover === false ? 'no' : 'pending'}`}>
                      {m.fullCover === true ? "Yes" : m.fullCover === false ? "No" : "Pending"}
                    </span>
                  </td>
                  <td>{new Date(m.createdAt).toLocaleString()}</td>
                  <td>
                    <span className={`status-badge ${m.systemStatus === 'active' ? 'active' : 'inactive'}`}>
                      {m.systemStatus || "active"}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons-cell">
                      <button
                        onClick={() => navigate(`/carmonitors/view/${m._id}`)}
                        className="icon-btn view"
                        title="View"
                      >
                        <Eye size={16} />
                      </button>
                      {m.systemStatus === 'active' ? (
                        <>
                          <button
                            onClick={() => handleFullCoverToggle(m)}
                            className={`icon-btn ${m.fullCover === null ? 'pending-btn' : 'edit'}`}
                            title={m.fullCover === null ? 'Set Full Cover' : 'Toggle Full Cover'}
                          >
                            {m.fullCover === null ? 'Set' : <Edit size={16} />}
                          </button>
                          <button
                            onClick={() => handleSoftDelete(m._id)}
                            className="icon-btn delete"
                            title="Deactivate"
                          >
                            <Trash2 size={16} />
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => handleRestore(m._id)}
                          className="icon-btn restore"
                          title="Activate"
                        >
                          <RotateCcw size={16} /> Activate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CarMonitors;